/**
 * SearchBar Component
 * 
 * Global search input with autocomplete support.
 */

import React, { useState, useCallback } from 'react';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';

const SearchContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 500px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 44px;
  border: 2px solid ${({ theme }) => theme.colors.border?.color || '#e5e7eb'};
  border-radius: 8px;
  font-size: 16px;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
  background-color: white;

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary || '#3b82f6'};
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  pointer-events: none;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #6b7280;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

interface SearchBarProps {
  placeholder?: string;
  initialValue?: string;
  onSearch?: (query: string) => void;
  navigateOnSearch?: boolean;
}

export function SearchBar({
  placeholder = 'Search products...',
  initialValue = '',
  onSearch,
  navigateOnSearch = true,
}: SearchBarProps) {
  const [value, setValue] = useState(initialValue);
  const navigate = useNavigate();

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const trimmedValue = value.trim();
      
      if (onSearch) {
        onSearch(trimmedValue);
      }
      
      if (navigateOnSearch && trimmedValue) {
        navigate(`/search?q=${encodeURIComponent(trimmedValue)}`);
      }
    },
    [value, onSearch, navigateOnSearch, navigate]
  );

  const handleClear = useCallback(() => {
    setValue('');
    if (onSearch) {
      onSearch('');
    }
  }, [onSearch]);

  return (
    <SearchContainer>
      <form onSubmit={handleSubmit}>
        <SearchIcon>
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </SearchIcon>
        <SearchInput
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
          aria-label="Search products"
        />
        {value && (
          <ClearButton type="button" onClick={handleClear} aria-label="Clear search">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </ClearButton>
        )}
      </form>
    </SearchContainer>
  );
}

export default SearchBar;

